import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '../../core/configuration/configuration.service';
import { Observable } from 'rxjs';
import { AddressOverviewData, CustomerData, CustomerDataStorageService } from '../../core/user/customer-data-storage.service';
import { map, tap } from 'rxjs/operators';
import {
  CUSTOMER_COMPANY_ENDPOINT,
  CUSTOMER_DATA_ENDPOINT,
  CUSTOMER_PAYMENT_DETAILS_ENDPOINT,
  CUSTOMER_SHIPPING_ADDRESS_ENDPOINT,
} from '../ladeloesung-api.constants';
import { Nullable, Optional } from '../../shared/shared.types';

export enum PaymentType {
  MLogin = 'mlogin',
  Invoice = 'invoice',
}

export interface PaymentDetails {
  paymentType: PaymentType;
  billingAddress?: Nullable<AddressOverviewData>;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerDataService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigurationService);
  private readonly customerDataStorageService = inject(CustomerDataStorageService);
  private readonly apiUrl: string = this.config.apiUrl;
  private readonly paymentDetailsUrl = `${this.apiUrl}${CUSTOMER_PAYMENT_DETAILS_ENDPOINT}`;

  public updateCompanyData(customerData: CustomerData): Observable<void> {
    return this.http
      .patch<void>(`${this.apiUrl}${CUSTOMER_COMPANY_ENDPOINT}`, {
        name: customerData?.company?.name,
        taxId: customerData?.company?.taxId,
      })
      .pipe(
        map(() => {
          this.customerDataStorageService.setCustomerData(customerData);
        }),
      );
  }

  public updateShippingAddressData(
    customerData: CustomerData,
    hasExtraAddress: boolean,
  ): Observable<void> {
    return this.http
      .patch<void>(`${this.apiUrl}${CUSTOMER_SHIPPING_ADDRESS_ENDPOINT}`, {
        address: hasExtraAddress ? customerData?.shippingAddress : null,
      })
      .pipe(
        map(() => {
          this.customerDataStorageService.setCustomerData(customerData);
        }),
      );
  }

  public updatePaymentDetails(
    paymentDetails: PaymentDetails,
  ): Observable<void> {
    return this.http.patch<void>(this.paymentDetailsUrl, paymentDetails);
  }

  public read(useCachedData = false): Observable<CustomerData> {
    if (useCachedData && this.customerDataStorageService.getCustomerData()) {
      return new Observable((observer) => {
        observer.next(this.customerDataStorageService.getCustomerData());
        observer.complete();
      });
    }
    return this.http.get<CustomerData>(`${this.apiUrl}${CUSTOMER_DATA_ENDPOINT}`).pipe(
      tap((customerData) => {
        this.customerDataStorageService.setCustomerData(customerData);
      }),
    );
  }


  public getPaymentDetails(): Observable<PaymentDetails> {
    return this.http.get<PaymentDetails>(this.paymentDetailsUrl);
  }
}

export function hasBillingAddress(paymentDetails: Optional<PaymentDetails>): boolean {
  return (
    !!paymentDetails?.billingAddress && !!paymentDetails?.billingAddress?.street
  );
}

export function isMLoginPaymentType(paymentType: Optional<PaymentType>): boolean {
  return paymentType === PaymentType.MLogin;
}
